import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiRequestError } from '../api/client'
import type { DecisionAction } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { number, percent, priorityClass, shortDate } from '../lib/format'
import { Avatar, CardSkeleton, ErrorState, PageHeader } from '../components/ui'
import './ReportDetail.css'

/**
 * Screen 7c · Admin · Şikayət detalı + qərar.
 *
 * The decision buttons come from the server (`availableDecisions`), so the
 * panel never offers an action the caller's role would be refused for.
 */

/** Block lengths offered by the "İstifadəçini blokla ▾" dropdown. */
const BLOCK_OPTIONS = [
  { days: 1, label: '1 gün' },
  { days: 7, label: '7 gün' },
  { days: 30, label: '30 gün' },
  { days: 365, label: '1 il' },
]

export function ReportDetail() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const id = Number(reportId)

  const { data, error, loading, reload } = useAsync(() => api.report(id), [id])

  const [note, setNote] = useState('')
  const [blockDays, setBlockDays] = useState(BLOCK_OPTIONS[1]!.days)
  const [submitting, setSubmitting] = useState<DecisionAction | null>(null)
  const [decisionError, setDecisionError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<{ next: number | null; remaining: number } | null>(null)
  const [reviewing, setReviewing] = useState(false)

  async function decide(action: DecisionAction) {
    setSubmitting(action)
    setDecisionError(null)
    try {
      const response = await api.decide(
        id,
        action,
        note.trim() || undefined,
        action === 'BLOCK_USER' ? blockDays : undefined,
      )
      // Screen 8e: confirm, then offer the next item in the queue.
      setOutcome({ next: response.nextInQueue ?? null, remaining: response.remainingInQueue })
    } catch (cause) {
      setDecisionError(
        cause instanceof ApiRequestError ? cause.message : 'Qərar yadda saxlanmadı.',
      )
    } finally {
      setSubmitting(null)
    }
  }

  /**
   * Handoff §8: an appeal is reviewed by a senior moderator or above. Upholding
   * keeps the original decision; overturning it also counts as a strike against
   * the moderator who made it, which the server records.
   */
  async function reviewAppeal(uphold: boolean) {
    setReviewing(true)
    setDecisionError(null)
    try {
      await api.reviewAppeal(id, uphold)
      reload()
    } catch (cause) {
      setDecisionError(
        cause instanceof ApiRequestError ? cause.message : 'Etiraza baxıla bilmədi.',
      )
    } finally {
      setReviewing(false)
    }
  }

  if (error) return <ErrorState error={error} onRetry={reload} />
  if (loading || !data) return <CardSkeleton height={420} />

  // The decision landed — screen 8e "qərar qeydə alındı".
  if (outcome) {
    return (
      <div className="decision-done card">
        <h2>Qərar qeydə alındı</h2>
        <p className="muted">
          #{data.id} bağlandı. Jurnala yazıldı və dəyişdirilə bilməz.
        </p>
        <p className="muted">
          Növbədə <span className="numeral">{number(outcome.remaining)}</span> şikayət qaldı.
        </p>
        <div className="row">
          {outcome.next !== null && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                setOutcome(null)
                setNote('')
                void navigate(`/novbe/${outcome.next}`)
              }}
            >
              Növbəti şikayət
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void navigate('/novbe')}
          >
            Növbəni bağla
          </button>
        </div>
      </div>
    )
  }

  const content = data.reportedContent
  const alreadyDecided = data.decision !== undefined && data.decision !== null

  return (
    <>
      <PageHeader
        title={`Şikayət #${data.id}`}
        subtitle={
          <>
            {data.reasonLabel} · @{data.reporter.username} şikayət etdi ·{' '}
            {shortDate(data.createdAt)}
          </>
        }
        actions={
          <>
            <span className={priorityClass(data.priority)}>{data.priorityLabel}</span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void navigate('/novbe')}
            >
              Növbəyə qayıt
            </button>
          </>
        }
      />

      <div className="detail">
        <div className="stack">
          {/* ŞİKAYƏT OLUNAN MƏZMUN */}
          <section className="card stack">
            <div className="overline">Şikayət olunan məzmun</div>

            {content.body ? (
              <blockquote className="detail__quote">“{content.body}”</blockquote>
            ) : (
              <p className="muted">İstifadəçi barədə şikayət — konkret məzmun göstərilməyib.</p>
            )}

            <div className="row row--between detail__author">
              <div className="row">
                <Avatar initials={content.author.initials} />
                <div>
                  <div className="detail__author-name">
                    {content.author.displayName} · @{content.author.username}
                  </div>
                  <div className="muted detail__author-meta">
                    {content.kind === 'ADVICE' ? 'Məsləhət' : content.kind === 'POST' ? 'Post' : 'İstifadəçi'}
                    {content.stars !== undefined && content.stars !== null && (
                      <> · <span className="numeral">{content.stars}</span> ulduz</>
                    )}
                  </div>
                </div>
              </div>

              {content.authorWarnings > 0 && (
                <span className="chip chip--medium">
                  Əvvəl <span className="chip__count">{content.authorWarnings}</span> xəbərdarlıq
                </span>
              )}
            </div>
          </section>

          {/* KONTEKST — ORİJİNAL POST */}
          {data.context && (
            <section className="card stack">
              <div className="overline">Kontekst — orijinal post</div>
              <blockquote className="detail__quote detail__quote--muted">
                “{data.context.body}”
              </blockquote>
              {data.context.result && (
                <div className="result-bar" aria-label="Səsvermə nəticəsi">
                  <div
                    className="result-bar__reason"
                    style={{ width: `${data.context.result.reasonPct}%` }}
                  >
                    <span className="numeral">{data.context.result.reasonPct}%</span>
                  </div>
                  <div
                    className="result-bar__excuse"
                    style={{ width: `${data.context.result.excusePct}%` }}
                  >
                    <span className="numeral">{data.context.result.excusePct}%</span>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* The offender's appeal (screen 6a), and its review. */}
          {data.appeal && (
            <section className="card stack detail__appeal">
              <div className="overline">Etiraz</div>
              <p className="detail__appeal-body">“{data.appeal.body}”</p>

              {data.appeal.status === 'PENDING' ? (
                <>
                  <p className="muted">
                    Etiraz baxılmayıb. Qərarı saxlaya və ya ləğv edə bilərsən —
                    ləğv qərarı verən moderatora səhv kimi yazılır.
                  </p>
                  <div className="row">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={reviewing}
                      onClick={() => void reviewAppeal(true)}
                    >
                      {reviewing ? 'Gözlə…' : 'Qərarı saxla'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      disabled={reviewing}
                      onClick={() => void reviewAppeal(false)}
                    >
                      {reviewing ? 'Gözlə…' : 'Qərarı ləğv et'}
                    </button>
                  </div>
                </>
              ) : (
                <p className="muted">
                  {data.appeal.status === 'UPHELD'
                    ? 'Etiraza baxıldı · qərar saxlanıldı.'
                    : 'Etiraza baxıldı · qərar ləğv olundu.'}
                </p>
              )}
            </section>
          )}

          {/* Qərar */}
          {alreadyDecided ? (
            <section className="card stack">
              <div className="overline">Qərar</div>
              <p className="detail__decided">
                {decisionLabel(data.decision!)}
                {data.resolvedByUsername && (
                  <span className="muted"> · @{data.resolvedByUsername}</span>
                )}
              </p>
              {data.decisionNote && (
                <>
                  <div className="overline">Daxili qeyd</div>
                  <p className="detail__note">{data.decisionNote}</p>
                </>
              )}
            </section>
          ) : (
            <section className="card stack">
              <div className="overline">Daxili qeyd</div>
              <textarea
                className="textarea"
                placeholder="Qərarın səbəbini yaz — yalnız moderatorlar görür…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              <div className="overline">Qərar</div>

              {decisionError && (
                <p className="detail__error" role="alert">
                  {decisionError}
                </p>
              )}

              <div className="detail__actions">
                {data.availableDecisions.map((option) =>
                  option.requiresBlockDays ? (
                    <div key={option.action} className="detail__block">
                      <select
                        className="select detail__block-select"
                        value={blockDays}
                        onChange={(e) => setBlockDays(Number(e.target.value))}
                        aria-label="Blok müddəti"
                      >
                        {BLOCK_OPTIONS.map((o) => (
                          <option key={o.days} value={o.days}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={submitting !== null}
                        onClick={() => void decide(option.action)}
                      >
                        {submitting === option.action ? 'Gözlə…' : option.label}
                      </button>
                    </div>
                  ) : (
                    <button
                      key={option.action}
                      type="button"
                      className={`btn btn--sm ${buttonClass(option.action)}`}
                      disabled={submitting !== null}
                      onClick={() => void decide(option.action)}
                    >
                      {submitting === option.action ? 'Gözlə…' : option.label}
                    </button>
                  ),
                )}
              </div>
            </section>
          )}
        </div>

        {/* Şikayətçi tarixçəsi */}
        <aside className="stack">
          <section className="card stack">
            <h3>Şikayətçi tarixçəsi</h3>
            <dl className="detail__stats">
              <div>
                <dt className="overline">Ümumi şikayət</dt>
                <dd className="numeral">{number(data.reporterAccuracy.total)}</dd>
              </div>
              <div>
                <dt className="overline">Təsdiqlənmiş</dt>
                <dd className="numeral">{number(data.reporterAccuracy.upheld)}</dd>
              </div>
              <div>
                <dt className="overline">Dəqiqlik</dt>
                <dd className="numeral">{percent(data.reporterAccuracy.accuracyPct)}</dd>
              </div>
              {/*
                Moderasiya balı — dəqiqlikdən fərqlidir: dəqiqlik nisbətdir,
                bal isə hər qərardan sonra dəyişən yığımdır (+2 / -8). 40-dan
                aşağı düşəndə bu şəxsin şikayətləri növbənin sonuna keçir, ona
                görə burada göstərilir.
              */}
              <div>
                <dt className="overline">Moderasiya balı</dt>
                <dd
                  className={`numeral${
                    data.reporterAccuracy.modScore < 40 ? ' detail__score--low' : ''
                  }`}
                >
                  {data.reporterAccuracy.modScore}/100
                </dd>
              </div>
            </dl>
            <p
              className={`detail__verdict detail__verdict--${
                (data.reporterAccuracy.accuracyPct ?? 0) >= 75 ? 'ok' : 'warn'
              }`}
            >
              {data.reporterAccuracy.verdictLabel}
            </p>
          </section>

          {data.note && (
            <section className="card stack">
              <div className="overline">Şikayətçinin qeydi</div>
              <p className="detail__note">{data.note}</p>
            </section>
          )}
        </aside>
      </div>
    </>
  )
}

function buttonClass(action: DecisionAction): string {
  switch (action) {
    case 'DELETE_CONTENT':
      return 'btn--danger'
    case 'REJECT':
      return 'btn--ghost'
    case 'WARN':
    case 'HIDE_POST':
      return 'btn--primary'
    case 'BLOCK_USER':
      return 'btn--danger'
  }
}

function decisionLabel(action: DecisionAction): string {
  switch (action) {
    case 'DELETE_CONTENT':
      return 'Məzmun silindi'
    case 'HIDE_POST':
      return 'Post gizlədildi'
    case 'WARN':
      return 'Xəbərdarlıq göndərildi'
    case 'BLOCK_USER':
      return 'İstifadəçi bloklandı'
    case 'REJECT':
      return 'Şikayət rədd edildi'
  }
}
